import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Search, Plus, Minus, Trash2, X, Package, CheckCircle2, Loader2, MessageCircle, Menu, Download, Mail, ArrowLeft, Share2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0A1F44";
const NAVY_2 = "#122E5E";
const BLUE = "#2D6BFF";
const BLUE_DARK = "#1E54E6";
const money = (n) => `Q ${Number(n || 0).toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FONTS_ID = "tienda-fonts";

const WA_NUMERO = "50251001314";

const WhatsAppFloat = ({ liftUp }) => (
  <a
    data-testid="whatsapp-float-button"
    href={`https://wa.me/${WA_NUMERO}?text=${encodeURIComponent("¡Hola! Vengo del catálogo en línea 🛒 Quisiera más información.")}`}
    target="_blank"
    rel="noreferrer"
    aria-label="Escríbenos por WhatsApp"
    className={`fixed right-4 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-150 ${liftUp ? "bottom-20" : "bottom-4"} sm:bottom-6 sm:right-6`}
    style={{ backgroundColor: "#25D366" }}
  >
    <svg viewBox="0 0 32 32" className="w-8 h-8" fill="#fff" aria-hidden="true">
      <path d="M16 3C9.373 3 4 8.373 4 15c0 2.25.62 4.354 1.7 6.156L4 29l8.05-1.652A11.94 11.94 0 0 0 16 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 21.6c-1.87 0-3.61-.51-5.1-1.4l-.36-.21-4.02.83.85-3.92-.24-.38A9.56 9.56 0 0 1 6.4 15c0-5.3 4.3-9.6 9.6-9.6s9.6 4.3 9.6 9.6-4.3 9.6-9.6 9.6zm5.27-7.19c-.29-.14-1.7-.84-1.97-.94-.26-.1-.46-.14-.65.15-.19.29-.74.94-.91 1.13-.17.19-.34.22-.62.07-.29-.14-1.21-.45-2.3-1.42-.85-.76-1.43-1.7-1.6-1.98-.17-.29-.02-.45.13-.59.13-.13.29-.34.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.56-.89-2.14-.23-.56-.47-.48-.65-.49h-.55c-.19 0-.5.07-.77.36-.26.29-1 .98-1 2.4 0 1.42 1.03 2.79 1.18 2.98.14.19 2.03 3.1 4.92 4.35.69.3 1.22.47 1.64.6.69.22 1.32.19 1.81.12.55-.08 1.7-.7 1.94-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34z"/>
    </svg>
  </a>
);
const useFonts = () => {
  useEffect(() => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement("link");
    link.id = FONTS_ID;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
};

const ProductImage = ({ src, alt, iconSize = "w-10 h-10" }) => {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [src]);
  if (!src || err) return <Package className={`${iconSize} text-slate-300`} strokeWidth={1.2} />;
  return <img src={src} alt={alt} loading="lazy" onError={() => setErr(true)} className="w-full h-full object-contain p-2" />;
};

const ProductCard = ({ p, onAdd, onOpen, inCart }) => {
  const [qty, setQty] = useState(1);
  return (
    <div data-testid={`producto-card-${p.sku}`} className="flex flex-col bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-transform duration-150">
      <button data-testid={`producto-image-${p.sku}`} onClick={() => onOpen(p)} className="relative h-28 sm:h-40 bg-white flex items-center justify-center overflow-hidden border-b border-slate-100 cursor-zoom-in w-full">
        <ProductImage src={p.imagen_url} alt={p.descripcion} />
        {p.video_url && (
          <span data-testid={`video-badge-${p.sku}`} className="absolute bottom-1.5 right-1.5 text-[10px] font-bold text-white bg-black/60 rounded-full px-2 py-0.5">▶ Video</span>
        )}
      </button>
      <div className="flex flex-col flex-1 p-2.5 sm:p-3 gap-1 text-center">
        <span className="text-[10px] font-semibold tracking-wide uppercase" style={{ color: BLUE }}>{p.categoria}</span>
        <p className="text-xs sm:text-sm font-medium leading-tight text-slate-800 line-clamp-2 min-h-[2.2em]" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
          {p.descripcion}
        </p>
        <p className="text-lg sm:text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: p.precio_anterior > 0 ? "#dc2626" : NAVY }}>
          {p.precio_anterior > 0 && <span className="text-xs font-semibold text-slate-400 line-through mr-1.5">{money(p.precio_anterior)}</span>}
          {money(p.precio)}
        </p>
        {p.oferta_etiqueta && <span className="mx-auto -mt-0.5 bg-red-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full" data-testid={`card-oferta-badge-${p.sku}`}>{p.oferta_etiqueta}</span>}
        <div className="flex items-center justify-center gap-2 mt-auto">
          <button data-testid={`card-qty-minus-${p.sku}`} onClick={() => setQty(Math.max(1, qty - 1))}
            className="w-8 h-8 border border-slate-300 rounded-md flex items-center justify-center hover:bg-slate-100 transition-colors"><Minus className="w-3.5 h-3.5" /></button>
          <span data-testid={`card-qty-value-${p.sku}`} className="text-sm font-bold w-8 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>{qty}</span>
          <button data-testid={`card-qty-plus-${p.sku}`} onClick={() => setQty(qty + 1)}
            className="w-8 h-8 border border-slate-300 rounded-md flex items-center justify-center hover:bg-slate-100 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
        </div>
        <button
          data-testid={`add-to-cart-${p.sku}`}
          onClick={() => { onAdd(p, qty); setQty(1); }}
          className="mt-1 w-full h-10 sm:h-9 text-sm font-semibold rounded-md text-white transition-colors duration-150"
          style={{ backgroundColor: inCart ? NAVY : BLUE }}
        >
          {qty > 1 ? `Agregar ${qty}` : inCart ? "✓ Agregar otro" : "Agregar"}
        </button>
      </div>
    </div>
  );
};

const ProductModal = ({ p, empresa, onClose, onAdd, inCart }) => {
  const [qty, setQty] = useState(1);
  useEffect(() => setQty(1), [p]);
  if (!p) return null;
  const shareUrl = `${API}/api/tienda/${empresa}/p/${encodeURIComponent(p.sku)}`;
  const shareText = `${p.descripcion} — ${money(p.precio)}\n${p.video_url ? "▶ Este producto tiene VIDEO — míralo y haz tu pedido aquí: " : "Míralo y haz tu pedido aquí: "}${shareUrl}`;
  const nativeShare = async () => {
    try { await navigator.share({ title: p.descripcion, text: shareText, url: shareUrl }); } catch {}
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[80]" onClick={onClose} />
      <div data-testid="product-modal" className="fixed inset-x-3 top-[4%] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[480px] max-h-[92vh] overflow-y-auto bg-white rounded-xl z-[90] shadow-2xl">
        <div className="flex items-center justify-between px-4 h-12" style={{ backgroundColor: NAVY }}>
          <p className="text-white text-xs font-semibold uppercase tracking-wide truncate pr-2" style={{ fontFamily: "Outfit, sans-serif" }}>{p.categoria}</p>
          <button data-testid="product-modal-close" onClick={onClose} className="p-1.5 text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-white flex items-center justify-center p-3" style={{ minHeight: p.video_url ? undefined : "260px" }}>
          <div className={`w-full ${p.video_url ? "h-[200px]" : "h-[280px] sm:h-[320px]"} flex items-center justify-center`}>
            <ProductImage src={p.imagen_url} alt={p.descripcion} iconSize="w-16 h-16" />
          </div>
        </div>
        {p.video_url && (
          <div className="px-3 pb-2">
            <video data-testid="product-video" src={p.video_url} controls playsInline preload="metadata"
              className="w-full max-h-[260px] rounded-md bg-black" />
          </div>
        )}
        <div className="px-4 pb-4 space-y-3">
          <p className="text-sm font-medium text-slate-800 leading-snug">{p.descripcion}</p>
          <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: p.precio_anterior > 0 ? "#dc2626" : NAVY }}>
            {p.precio_anterior > 0 && <span className="text-sm font-semibold text-slate-400 line-through mr-2">{money(p.precio_anterior)}</span>}
            {money(p.precio)}
            {p.oferta_etiqueta && <span className="ml-2 align-middle bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{p.oferta_etiqueta}</span>}
          </p>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">Cantidad:</span>
            <div className="flex items-center gap-2">
              <button data-testid="modal-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-10 h-10 border border-slate-300 rounded-md flex items-center justify-center hover:bg-slate-100 transition-colors"><Minus className="w-4 h-4" /></button>
              <span data-testid="modal-qty-value" className="text-lg font-bold w-10 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>{qty}</span>
              <button data-testid="modal-qty-plus" onClick={() => setQty(qty + 1)}
                className="w-10 h-10 border border-slate-300 rounded-md flex items-center justify-center hover:bg-slate-100 transition-colors"><Plus className="w-4 h-4" /></button>
            </div>
            <span className="ml-auto text-sm font-semibold text-slate-700" data-testid="modal-qty-subtotal">{money(p.precio * qty)}</span>
          </div>
          {p.existencia != null && qty > p.existencia && (
            <p data-testid="modal-stock-warning" className="text-[11px] leading-tight text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              ⚠ Solo hay {p.existencia} disponible{p.existencia !== 1 ? "s" : ""}; el resto queda sujeto a confirmación.
            </p>
          )}
          <button data-testid="modal-add-to-cart" onClick={() => { onAdd(p, qty); onClose(); }}
            className="w-full h-11 text-sm font-bold rounded-md text-white transition-colors" style={{ backgroundColor: inCart ? NAVY : BLUE }}>
            {qty > 1 ? `Agregar ${qty} al carrito — ${money(p.precio * qty)}` : "Agregar al carrito"}
          </button>
          <div className="grid grid-cols-3 gap-2">
            <a data-testid="share-whatsapp-button" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"
              className="h-10 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
              <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
            </a>
            <a data-testid="share-email-button" href={`mailto:?subject=${encodeURIComponent(p.descripcion)}&body=${encodeURIComponent(shareText)}`}
              className="h-10 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
              <Mail className="w-4 h-4 text-blue-600" /> Correo
            </a>
            {p.imagen_url ? (
              <a data-testid="download-image-button" href={`${API}/api/tienda/${empresa}/p/${encodeURIComponent(p.sku)}/imagen`}
                className="h-10 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                <Download className="w-4 h-4" /> Imagen
              </a>
            ) : (
              <button onClick={nativeShare} data-testid="share-native-button"
                className="h-10 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                <Share2 className="w-4 h-4" /> Compartir
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const CategoryMenu = ({ open, onClose, categorias, cat, setCat }) => (
  <>
    <div className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
    <div data-testid="category-menu" className={`fixed top-0 left-0 h-full w-[280px] z-[70] flex flex-col transition-transform duration-200 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`} style={{ backgroundColor: NAVY }}>
      <div className="flex items-center justify-between px-4 h-14 border-b border-white/10">
        <h2 className="text-white font-bold tracking-wide text-sm uppercase" style={{ fontFamily: "Outfit, sans-serif" }}>Categorías</h2>
        <button data-testid="menu-close-button" onClick={onClose} className="p-2 text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        <button
          data-testid="menu-cat-todas"
          onClick={() => { setCat(""); onClose(); }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${!cat ? "text-white" : "text-white/60 hover:text-white"}`}
          style={!cat ? { backgroundColor: BLUE } : {}}
        >
          Todas las categorías
        </button>
        {categorias.map((c) => (
          <button
            key={c}
            onClick={() => { setCat(c); onClose(); }}
            className={`w-full text-left px-4 py-2.5 text-sm font-medium uppercase transition-colors ${cat === c ? "text-white" : "text-white/60 hover:text-white"}`}
            style={cat === c ? { backgroundColor: BLUE } : {}}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  </>
);

const CartDrawer = ({ open, onClose, cart, setQty, removeItem, empresa, empresaNombre, onSuccess }) => {
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", nota: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const subtotal = cart.reduce((s, i) => s + i.precio * i.cantidad, 0);

  const submit = async () => {
    setError("");
    if (!form.nombre.trim() || !form.telefono.trim()) {
      setError("Ingresa tu nombre y teléfono para continuar.");
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("El correo electrónico no es válido.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/tienda/${empresa}/pedido`, {
        nombre: form.nombre, telefono: form.telefono, email: form.email, nota: form.nota,
        items: cart.map((i) => ({ sku: i.sku, harperp_id: i.id, descripcion: i.descripcion, precio: i.precio, cantidad: i.cantidad, imagen_url: i.imagen_url })),
      });
      onSuccess(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar el pedido. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
      <div data-testid="cart-drawer" className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white z-[70] flex flex-col transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-4 h-14" style={{ backgroundColor: NAVY }}>
          <h2 className="text-lg font-bold tracking-tight text-white flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <ShoppingCart className="w-5 h-5" /> Tu carrito ({cart.length})
          </h2>
          <button data-testid="cart-close-button" onClick={onClose} className="p-2 text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
          {cart.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <ShoppingCart className="w-10 h-10 mx-auto mb-3" strokeWidth={1.2} />
              <p className="text-sm">Tu carrito está vacío</p>
            </div>
          )}
          {cart.map((i) => (
            <div key={i.sku} className="flex gap-3 bg-white border border-slate-200 rounded-lg p-2" data-testid={`cart-item-${i.sku}`}>
              <div className="w-16 h-16 bg-white border border-slate-100 rounded flex items-center justify-center shrink-0 overflow-hidden">
                <ProductImage src={i.imagen_url} alt="" iconSize="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-tight line-clamp-2 text-slate-800">{i.descripcion}</p>
                <p className="text-sm font-bold mt-0.5" style={{ color: NAVY }}>{money(i.precio * i.cantidad)}</p>
                <div className="flex items-center gap-2 mt-1">
                  <button data-testid={`qty-minus-${i.sku}`} onClick={() => setQty(i.sku, i.cantidad - 1)} className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="text-sm font-semibold w-6 text-center" data-testid={`qty-value-${i.sku}`}>{i.cantidad}</span>
                  <button data-testid={`qty-plus-${i.sku}`} onClick={() => setQty(i.sku, i.cantidad + 1)} className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                  <button data-testid={`remove-item-${i.sku}`} onClick={() => removeItem(i.sku)} className="ml-auto p-1.5 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                {i.existencia != null && i.cantidad > i.existencia && (
                  <p data-testid={`stock-warning-${i.sku}`} className="mt-1.5 text-[11px] leading-tight text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠ Solo hay {i.existencia} disponible{i.existencia !== 1 ? "s" : ""}; el resto queda sujeto a confirmación.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-slate-200 p-4 space-y-2.5 bg-white">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-slate-500">Subtotal</span>
              <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: NAVY }} data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <input data-testid="checkout-name-input" placeholder="Tu nombre *" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2D6BFF] focus:border-[#2D6BFF]" />
            <input data-testid="checkout-phone-input" placeholder="Tu teléfono / WhatsApp *" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2D6BFF] focus:border-[#2D6BFF]" />
            <input data-testid="checkout-email-input" type="email" placeholder="Correo electrónico (opcional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2D6BFF] focus:border-[#2D6BFF]" />
            <textarea data-testid="checkout-note-input" placeholder="Nota (opcional)" rows={2} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2D6BFF] focus:border-[#2D6BFF] resize-none" />
            {error && <p className="text-xs text-red-600" data-testid="checkout-error">{error}</p>}
            <button data-testid="checkout-submit-button" onClick={submit} disabled={sending}
              className="w-full h-12 text-white font-bold text-sm rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: BLUE }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              Solicitar cotización
            </button>
            <p className="text-[11px] text-slate-500 text-center">Un asesor de {empresaNombre} te contactará por WhatsApp.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default function TiendaPublica() {
  useFonts();
  const { empresa } = useParams();
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [homeCfg, setHomeCfg] = useState(null);
  const [bannerIdx, setBannerIdx] = useState(0);
  const heroTouchX = useRef(null);
  const [ofertaIdx, setOfertaIdx] = useState(0);
  const ofertasConArte = (homeCfg?.ofertas || []).filter((o) => o.oferta_imagen);

  useEffect(() => {
    fetch(`${API}/api/tienda/${empresa}/home`).then((r) => r.json()).then(setHomeCfg).catch(() => {});
  }, [empresa]);

  useEffect(() => {
    if (!homeCfg?.banners || homeCfg.banners.length < 2) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % homeCfg.banners.length), 5000);
    return () => clearInterval(t);
  }, [homeCfg]);

  useEffect(() => {
    const n = (homeCfg?.ofertas || []).filter((o) => o.oferta_imagen).length;
    if (n < 2) return;
    const t = setInterval(() => setOfertaIdx((i) => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [homeCfg]);
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`tienda_cart_${window.location.pathname.split("/")[2] || ""}`) || "null");
      if (saved && Array.isArray(saved.items) && Date.now() - (saved.ts || 0) < 48 * 3600 * 1000) return saved.items;
    } catch { /* noop */ }
    return [];
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pedidoResult, setPedidoResult] = useState(null);
  const [modalProduct, setModalProduct] = useState(null);
  const [fromCrm] = useState(() => new URLSearchParams(window.location.search).get("from") === "crm");
  const sharedSkuRef = useRef(new URLSearchParams(window.location.search).get("p"));
  const [bounce, setBounce] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    try {
      const key = `tienda_cart_${empresa}`;
      if (cart.length) localStorage.setItem(key, JSON.stringify({ ts: Date.now(), items: cart }));
      else localStorage.removeItem(key);
    } catch { /* noop */ }
  }, [cart, empresa]);

  const fetchProductos = useCallback(async (pg, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    setErrorMsg("");
    try {
      const { data } = await axios.get(`${API}/api/tienda/${empresa}/catalogo`, {
        params: { q: q || undefined, categoria: cat || undefined, page: pg, per_page: 24 },
      });
      setProductos((prev) => (append ? [...prev, ...data.data] : data.data));
      setCategorias(data.categorias || []);
      setEmpresaNombre(data.empresa || "");
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(pg);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "No se pudo cargar el catálogo.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [empresa, q, cat]);

  useEffect(() => { fetchProductos(1); }, [fetchProductos]);

  const trackedRef = useRef(false);
  useEffect(() => {
    if (fromCrm || trackedRef.current) return;
    trackedRef.current = true;
    const device = /Mobi|Android/i.test(navigator.userAgent) ? "movil" : "escritorio";
    axios.post(`${API}/api/tienda/${empresa}/track`, { tipo: "visita", device }).catch(() => {});
  }, [empresa, fromCrm]);

  const openProduct = (p) => {
    setModalProduct(p);
    if (!fromCrm) {
      const device = /Mobi|Android/i.test(navigator.userAgent) ? "movil" : "escritorio";
      axios.post(`${API}/api/tienda/${empresa}/track`, { tipo: "producto", sku: p.sku, device }).catch(() => {});
    }
  };

  const onSearchChange = (v) => {
    setQInput(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(v), 400);
  };

  const elegirCategoria = (c) => {
    clearTimeout(debounceRef.current);
    setQInput("");
    setQ("");
    setCat(c);
  };

  const addToCart = (p, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.sku === p.sku);
      if (found) return prev.map((i) => (i.sku === p.sku ? { ...i, cantidad: i.cantidad + qty } : i));
      return [...prev, { ...p, cantidad: qty }];
    });
    setBounce(true);
    setTimeout(() => setBounce(false), 300);
  };
  const setQty = (sku, qty) => {
    if (qty <= 0) return removeItem(sku);
    setCart((prev) => prev.map((i) => (i.sku === sku ? { ...i, cantidad: qty } : i)));
  };
  const removeItem = (sku) => setCart((prev) => prev.filter((i) => i.sku !== sku));
  const cartCount = cart.reduce((s, i) => s + i.cantidad, 0);

  if (pedidoResult) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ fontFamily: "IBM Plex Sans, sans-serif", backgroundColor: "#EEF1F6" }}>
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8 text-center shadow-lg" data-testid="pedido-success">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" strokeWidth={1.5} style={{ color: BLUE }} />
          <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "Outfit, sans-serif", color: NAVY }}>¡Pedido recibido!</h1>
          <p className="text-sm text-slate-600 mb-1">Tu solicitud de cotización</p>
          <p className="text-3xl font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: BLUE }} data-testid="pedido-numero">{pedidoResult.numero}</p>
          {(pedidoResult.advertencias || []).length > 0 && (
            <div data-testid="pedido-advertencias" className="mb-4 text-left text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
              <p className="font-semibold">⚠ Algunos productos exceden la existencia actual:</p>
              {pedidoResult.advertencias.map((a) => (
                <p key={a.sku}>• {a.descripcion}: pediste {a.pedido}, hay {a.disponible}</p>
              ))}
              <p>Tu asesor te confirmará la disponibilidad del resto.</p>
            </div>
          )}
          <p className="text-sm text-slate-600 mb-4">Un asesor de <strong>{empresaNombre}</strong> te contactará por WhatsApp muy pronto para confirmar tu pedido.</p>
          <a
            data-testid="confirmar-whatsapp-button"
            href={`https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(`¡Hola! Acabo de hacer el pedido ${pedidoResult.numero} en la tienda en línea 🛒 Quedo atento a la confirmación.`)}`}
            target="_blank"
            rel="noreferrer"
            className="w-full h-12 mb-3 text-white font-bold text-sm rounded-md transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: "#25D366" }}
          >
            <MessageCircle className="w-4 h-4" /> Confirmar mi pedido por WhatsApp
          </a>
          <button data-testid="volver-tienda-button" onClick={() => { setPedidoResult(null); setCart([]); setDrawerOpen(false); }}
            className="w-full h-12 text-white font-bold text-sm rounded-md transition-colors" style={{ backgroundColor: NAVY }}>
            Seguir viendo el catálogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ fontFamily: "IBM Plex Sans, sans-serif", backgroundColor: "#EEF1F6" }}>
      {/* Header navy */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-3">
          {fromCrm && (
            <a data-testid="volver-crm-button" href="/tienda-admin"
              className="flex items-center gap-1 h-9 px-2.5 rounded-md text-xs font-semibold text-white whitespace-nowrap" style={{ backgroundColor: BLUE }}>
              <ArrowLeft className="w-4 h-4" /> CRM
            </a>
          )}
          <button data-testid="menu-toggle-button" onClick={() => setMenuOpen(true)} className="p-2 -ml-2 text-white/80 hover:text-white transition-colors">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 hidden sm:block" style={{ color: BLUE }} />
            <h1 className="text-sm sm:text-lg font-extrabold tracking-tight text-white uppercase whitespace-nowrap" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="tienda-nombre">
              Catálogo <span style={{ color: "#5B8CFF" }}>{empresaNombre ? empresaNombre.split(" ")[0] : ""}</span>
            </h1>
          </div>
          <div className="flex-1 relative hidden md:block max-w-xl mx-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="tienda-search-input" placeholder="Buscar producto o SKU..." value={qInput} onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-10 pl-9 pr-9 rounded-md text-sm bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 focus:ring-[#5B8CFF]" />
            {qInput && (
              <button data-testid="search-clear-btn" onClick={() => { clearTimeout(debounceRef.current); setQInput(""); setQ(""); }} aria-label="Limpiar búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 flex items-center justify-center text-sm font-bold transition-colors">×</button>
            )}
          </div>
          <button data-testid="cart-drawer-toggle" onClick={() => setDrawerOpen(true)}
            className="ml-auto relative h-10 px-3 sm:px-4 text-white rounded-md flex items-center gap-2 transition-colors" style={{ backgroundColor: BLUE }}>
            <ShoppingCart className={`w-4 h-4 ${bounce ? "scale-125" : "scale-100"} transition-transform duration-150`} />
            <span className="text-sm font-semibold hidden sm:inline">Carrito</span>
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className={`absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-white text-[11px] font-bold rounded-full flex items-center justify-center ${bounce ? "scale-125" : "scale-100"} transition-transform duration-150`} style={{ color: BLUE }}>
                {cartCount}
              </span>
            )}
          </button>
        </div>
        {/* Búsqueda móvil */}
        <div className="md:hidden px-3 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="tienda-search-input-mobile" placeholder="Buscar producto o SKU..." value={qInput} onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-11 pl-9 pr-9 rounded-md text-sm bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 focus:ring-[#5B8CFF]" />
            {qInput && (
              <button data-testid="search-clear-btn-mobile" onClick={() => { clearTimeout(debounceRef.current); setQInput(""); setQ(""); }} aria-label="Limpiar búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 flex items-center justify-center text-base font-bold transition-colors">×</button>
            )}
          </div>
        </div>
      </header>

      {/* Hero banners portada — texto izq + imagen der (estilo e-commerce) */}
      {homeCfg?.banners?.length > 0 && (
        <section className="relative overflow-hidden select-none" data-testid="tienda-hero" style={{ background: `linear-gradient(105deg, ${NAVY} 0%, ${NAVY_2} 60%, #1b3f7a 100%)` }}
          onTouchStart={(e) => { heroTouchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (heroTouchX.current === null) return;
            const dx = e.changedTouches[0].clientX - heroTouchX.current;
            heroTouchX.current = null;
            const n = homeCfg.banners.length;
            if (n < 2 || Math.abs(dx) < 45) return;
            setBannerIdx((i) => (dx < 0 ? (i + 1) % n : (i - 1 + n) % n));
          }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 sm:grid-cols-2 items-center gap-2 sm:gap-8 py-4 sm:py-8">
            <div className="order-2 sm:order-1 text-center sm:text-left pb-3 sm:pb-0">
              <p className="text-[10px] sm:text-xs font-black tracking-[0.25em] uppercase text-sky-300 mb-1 sm:mb-2">Complementos y Herrajes</p>
              <h2 className="text-white text-2xl sm:text-4xl font-black leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                {homeCfg.banners[bannerIdx]?.titulo || "Todo para tu proyecto con precios de mayorista"}
              </h2>
              <p className="text-white/75 text-xs sm:text-base mt-1.5 sm:mt-3">
                {homeCfg.banners[bannerIdx]?.subtitulo || "Herrajes, chapas, silicones, herrajes de vidrio templado y más. Pide en línea y recibe atención por WhatsApp."}
              </p>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-3 sm:mt-5">
                <button data-testid="hero-cta-ofertas" onClick={() => { elegirCategoria(""); window.scrollTo({ top: 0 }); }}
                  className="h-10 px-5 rounded-lg text-sm font-black text-slate-900 bg-white hover:bg-slate-100 transition-colors">🛒 Comprar ahora</button>
                <button data-testid="hero-cta-categorias" onClick={() => setMenuOpen(true)}
                  className="h-10 px-5 rounded-lg text-sm font-bold text-white border border-white/40 hover:bg-white/10 transition-colors">Ver categorías</button>
              </div>
            </div>
            <div className="order-1 sm:order-2 flex items-center justify-center">
              <img src={homeCfg.banners[bannerIdx]?.url} alt={homeCfg.banners[bannerIdx]?.titulo || "Banner"}
                className="max-w-full h-44 sm:h-[320px] object-contain drop-shadow-2xl rounded-lg" draggable={false} />
            </div>
          </div>
          {homeCfg.banners.length > 1 && (
            <>
              <button onClick={() => setBannerIdx((i) => (i - 1 + homeCfg.banners.length) % homeCfg.banners.length)}
                aria-label="Banner anterior" data-testid="hero-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setBannerIdx((i) => (i + 1) % homeCfg.banners.length)}
                aria-label="Banner siguiente" data-testid="hero-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center transition-colors">
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </button>
              <div className="absolute bottom-2.5 right-4 flex gap-1.5">
                {homeCfg.banners.map((_, i) => (
                  <button key={i} onClick={() => setBannerIdx(i)} aria-label={`Banner ${i + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors ${i === bannerIdx ? "bg-white" : "bg-white/40"}`} />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Franja de beneficios */}
      <section className="bg-white border-b border-slate-200" data-testid="tienda-beneficios">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-4 grid grid-cols-3 gap-2 sm:gap-6">
          {[
            ["🚚", "Envíos a todo el país", "Recibe donde estés"],
            ["🏷️", "Precios de mayorista", "Ahorra en cada compra"],
            ["💬", "Atención por WhatsApp", "Te asesoramos al instante"],
          ].map(([ic, t, s]) => (
            <div key={t} className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 text-center sm:text-left">
              <span className="text-xl sm:text-3xl">{ic}</span>
              <div>
                <p className="text-[10px] sm:text-sm font-black text-slate-800 leading-tight">{t}</p>
                <p className="hidden sm:block text-xs text-slate-500">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Marcas que manejamos */}
      {homeCfg?.marcas?.length > 0 && (
        <section className="bg-slate-50 border-b border-slate-200" data-testid="tienda-marcas">
          <div className="max-w-7xl mx-auto px-3 sm:px-8 py-4 sm:py-5">
            <p className="text-center text-[10px] sm:text-xs font-black tracking-[0.25em] uppercase text-slate-400 mb-3">Marcas que manejamos</p>
            <div className="flex items-center justify-center gap-5 sm:gap-10 flex-wrap">
              {homeCfg.marcas.map((m, i) => (
                <img key={i} src={m.url} alt={m.nombre || 'Marca'} title={m.nombre || ''} loading="lazy"
                  className="h-[37px] sm:h-[55px] object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Banner NUESTROS PRODUCTOS */}
      <div className="py-2.5 text-center" style={{ backgroundColor: NAVY_2 }}>
        <p className="text-white text-xs sm:text-sm font-bold tracking-[0.2em] uppercase" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="banner-productos">
          {cat || "Nuestros Productos"}
        </p>
      </div>

      {/* Menú de familias (todas las resoluciones) */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-6 py-2">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <button data-testid="familias-menu-btn" onClick={() => setMenuOpen(true)}
            className="flex-1 sm:flex-none sm:min-w-[260px] h-10 flex items-center justify-between gap-6 px-3 rounded-lg border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors">
            <span className="flex items-center gap-2">☰ {cat ? cat : "Elegir: Categoría"}</span>
            <span className="text-slate-400">▾</span>
          </button>
          {cat && (
            <button data-testid="familias-limpiar-btn" onClick={() => elegirCategoria("")}
              className="h-10 px-3 rounded-lg text-xs font-black text-white transition-opacity hover:opacity-90" style={{ backgroundColor: NAVY }}>
              TODAS
            </button>
          )}
        </div>
      </div>

      {/* Banner rotativo de ofertas con arte diseñado (se oculta al filtrar familia/búsqueda) */}
      {ofertasConArte.length > 0 && !cat && !q && (() => {
        const o = ofertasConArte[ofertaIdx % ofertasConArte.length];
        return (
          <section className="bg-slate-900" data-testid="tienda-ofertas-banner">
            <div className="max-w-7xl mx-auto relative">
              <button onClick={() => { setQInput(o.sku); setQ(o.sku); setCat(""); }} data-testid={`oferta-banner-slide-${o.sku}`}
                className="block w-full relative overflow-hidden">
                <img src={o.oferta_imagen} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
                <img src={o.oferta_imagen} alt={o.descripcion} className="relative w-full max-h-72 sm:max-h-96 object-contain mx-auto"
                  onError={(e) => { const s = e.currentTarget.closest("section"); if (s) s.style.display = "none"; }} />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3 flex items-end justify-between gap-3 text-left">
                  <div className="min-w-0">
                    <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{o.etiqueta}</span>
                    <p className="text-white text-xs sm:text-sm font-bold mt-1 line-clamp-1">{o.descripcion}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {o.precio_anterior > 0 && (
                      <p className="text-white/60 text-[11px] line-through" data-testid={`oferta-banner-precio-anterior-${o.sku}`}>Q {Number(o.precio_anterior).toLocaleString("es-GT", { minimumFractionDigits: 2 })}</p>
                    )}
                    <p className="text-white text-lg sm:text-2xl font-black" data-testid={`oferta-banner-precio-${o.sku}`}>Q {Number(o.precio || 0).toLocaleString("es-GT", { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </button>
              {ofertasConArte.length > 1 && (
                <div className="absolute top-2 right-3 flex gap-1.5">
                  {ofertasConArte.map((_, i) => (
                    <button key={i} onClick={() => setOfertaIdx(i)} aria-label={`Oferta ${i + 1}`}
                      className={`w-2 h-2 rounded-full transition-colors ${i === ofertaIdx % ofertasConArte.length ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Ofertas (compactas cuando hay familia o búsqueda activa) */}
      {homeCfg?.ofertas?.length > 0 && (() => {
        const compacta = !!(cat || q);
        return (
        <section className="bg-white border-b border-slate-200" data-testid="tienda-ofertas">
          <div className={`max-w-7xl mx-auto px-3 sm:px-6 ${compacta ? "py-2" : "py-4"}`}>
            <p className={`font-black text-slate-900 ${compacta ? "text-xs mb-1.5" : "text-sm mb-3"}`} style={{ fontFamily: "Outfit, sans-serif" }}>🔥 OFERTAS DESTACADAS</p>
            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1">
              {homeCfg.ofertas.map((p) => (
                <button key={p.sku} onClick={() => { setQInput(p.sku); setQ(p.sku); setCat(""); }} data-testid={`oferta-card-${p.sku}`}
                  className={`shrink-0 ${compacta ? "w-24" : "w-32 sm:w-40"} border border-slate-200 rounded-xl overflow-hidden text-left hover:shadow-md transition-shadow bg-white relative`}>
                  <span className={`absolute top-1 left-1 bg-red-600 text-white font-black px-1.5 py-0.5 rounded-full z-10 ${compacta ? "text-[8px]" : "text-[10px]"}`}>{p.etiqueta}</span>
                  {(p.oferta_imagen || p.imagen_url) ? <img src={p.oferta_imagen || p.imagen_url} alt={p.descripcion} className={`w-full ${compacta ? "h-14" : "h-20 sm:h-28"} ${p.oferta_imagen ? "object-cover" : "object-contain"} bg-slate-50`} /> : <div className={`w-full ${compacta ? "h-14" : "h-20 sm:h-28"} bg-slate-100`} />}
                  <div className={compacta ? "p-1.5" : "p-2.5"}>
                    {!compacta && <p className="text-[11px] font-bold text-slate-800 line-clamp-2">{p.descripcion}</p>}
                    {p.precio_anterior > 0 && (
                      <p className={`text-slate-400 line-through ${compacta ? "text-[9px]" : "text-[11px] mt-1"}`} data-testid={`oferta-precio-anterior-${p.sku}`}>Q {Number(p.precio_anterior).toLocaleString("es-GT", { minimumFractionDigits: 2 })}</p>
                    )}
                    <p className={`font-black ${compacta ? "text-[11px]" : "text-sm"} ${p.precio_anterior > 0 ? "text-red-600" : ""} ${p.precio_anterior > 0 || compacta ? "" : "mt-1"}`} style={p.precio_anterior > 0 ? {} : { color: NAVY }}>Q {Number(p.precio || 0).toLocaleString("es-GT", { minimumFractionDigits: 2 })}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {!loading && !errorMsg && (
          <p className="text-xs text-slate-500 mb-3" data-testid="resultados-count">{total} producto{total !== 1 ? "s" : ""} disponible{total !== 1 ? "s" : ""}{cat ? ` en ${cat}` : ""}</p>
        )}
        {loading ? (
          <div className="flex flex-col items-center py-24 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: BLUE }} />
            <p className="text-sm">Cargando catálogo...</p>
          </div>
        ) : errorMsg ? (
          <div className="text-center py-24 text-slate-500" data-testid="tienda-error">{errorMsg}</div>
        ) : productos.length === 0 ? (
          <div className="text-center py-24 text-slate-400" data-testid="sin-resultados">
            <Package className="w-10 h-10 mx-auto mb-3" strokeWidth={1.2} />
            <p className="text-sm">No hay productos que coincidan con tu búsqueda.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4 lg:gap-5" data-testid="productos-grid">
              {productos.map((p) => (
                <ProductCard key={`${p.sku}-${p.id}`} p={p} onAdd={addToCart} onOpen={openProduct} inCart={cart.some((i) => i.sku === p.sku)} />
              ))}
            </div>
            {page < pages && (
              <div className="text-center mt-6">
                <button data-testid="cargar-mas-button" onClick={() => fetchProductos(page + 1, true)} disabled={loadingMore}
                  className="h-11 px-8 font-semibold text-sm rounded-md text-white transition-colors disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                  {loadingMore ? "Cargando..." : "Cargar más productos"}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Ideas y usos */}
      {homeCfg?.ideas?.length > 0 && (
        <section className="bg-white border-t border-slate-200" data-testid="tienda-ideas">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
            <h2 className="font-black text-lg sm:text-xl text-slate-900 text-center mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>💡 Ideas y usos de nuestros herrajes</h2>
            <p className="text-xs text-slate-500 text-center mb-5">Inspírate con proyectos reales hechos con nuestros productos</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {homeCfg.ideas.map((it, i) => (
                <figure key={i} className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50" data-testid={`idea-pub-${i}`}>
                  <img src={it.url} alt={it.caption || "Idea de uso"} className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" />
                  {it.caption && <figcaption className="text-[11px] font-semibold text-slate-600 px-2.5 py-2">{it.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonios */}
      {homeCfg?.testimonios?.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200" data-testid="tienda-testimonios">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
            <h2 className="font-black text-lg sm:text-xl text-slate-900 text-center mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>⭐ Lo que dicen nuestros clientes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {homeCfg.testimonios.map((t, i) => (
                <figure key={t.id || i} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col" data-testid={`testi-pub-${i}`}>
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(Math.min(5, t.estrellas || 5))].map((_, j) => (
                      <svg key={j} width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.2l7.1-.6z"/></svg>
                    ))}
                  </div>
                  <blockquote className="text-xs text-slate-600 leading-relaxed flex-1">"{t.texto}"</blockquote>
                  <figcaption className="mt-3 pt-2 border-t border-slate-100 text-xs font-black text-slate-800">{t.nombre}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="py-6 text-center" style={{ backgroundColor: NAVY }}>
        <p className="text-xs text-white/60">{empresaNombre} — Catálogo en línea. Precios en Quetzales, sujetos a confirmación.</p>
      </footer>

      {/* Botón carrito flotante móvil */}
      {cartCount > 0 && !drawerOpen && (
        <button data-testid="floating-cart-button" onClick={() => setDrawerOpen(true)}
          className="sm:hidden fixed bottom-4 left-4 right-4 h-12 z-50 text-white font-bold text-sm rounded-md flex items-center justify-center gap-2 shadow-xl" style={{ backgroundColor: BLUE }}>
          <ShoppingCart className="w-4 h-4" />
          Ver carrito ({cartCount}) — {money(cart.reduce((s, i) => s + i.precio * i.cantidad, 0))}
        </button>
      )}

      {/* Botón flotante WhatsApp */}
      <WhatsAppFloat liftUp={cartCount > 0 && !drawerOpen} />

      <ProductModal p={modalProduct} empresa={empresa} onClose={() => setModalProduct(null)} onAdd={addToCart} inCart={modalProduct ? cart.some((i) => i.sku === modalProduct.sku) : false} />
      <CategoryMenu open={menuOpen} onClose={() => setMenuOpen(false)} categorias={categorias} cat={cat} setCat={elegirCategoria} />
      <CartDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} cart={cart} setQty={setQty} removeItem={removeItem}
        empresa={empresa} empresaNombre={empresaNombre} onSuccess={(data) => setPedidoResult(data)} />
    </div>
  );
}
